import type { ReactNode } from 'react';

import { faSpinner } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';

import type { TVisitSummary } from '@/react-query/useVisitSummaries';

import { previewDisplayDeviceAtom } from '../../multiFileViewer/imageToolbar/atoms';
import type { ImageObject } from '../../types';
import { getTeethFilteredByImageView } from '../../visitSidebar/helpers';
import { selectedTeethPreviewAtom } from '../atoms';

import { PatientDownloadableImage } from './PatientDownloadableImage';
import { PatientPrintoutLegend } from './PatientPrintoutLegend/PatientPrintoutLegend';
import { PatientPrintoutTooth } from './PatientPrintoutTooth';

interface VisitSectionProps {
  images: ImageObject[];
  title: ReactNode;
  position?: 'left' | 'right';
  visitSummary?: TVisitSummary;
  isLoading?: boolean;
}

export const VisitSection = ({
  images,
  title,
  position,
  isLoading,
  visitSummary,
}: VisitSectionProps) => {
  const previewDisplayDevice = useAtomValue(previewDisplayDeviceAtom);
  const selectedTeethPreview = useAtomValue(selectedTeethPreviewAtom);
  const isMobile = previewDisplayDevice === 'mobile';

  if (isLoading) {
    // show spiner
    return (
      <div className="vai-flex vai-items-center vai-justify-center vai-h-full">
        <FontAwesomeIcon
          icon={faSpinner}
          className="vai-h-7 vai-w-7 vai-text-zinc-200 vai-animate-spin"
        />
      </div>
    );
  }

  return (
    <div className="vai-bg-slate-50 vai-rounded-lg vai-overflow-hidden vai-border vai-border-slate-200">
      <div className="vai-bg-slate-200 vai-py-2 vai-px-4 vai-border-b vai-border-slate-200">
        <p className="vai-font-inter vai-font-semibold">{title}</p>
      </div>
      <div className="vai-py-4 vai-px-6">
        <div>
          <p className="vai-text-slate-600 vai-font-semibold vai-text-[12px] vai-uppercase vai-text-center vai-mb-4">
            <Trans>Findings</Trans>
          </p>
          <PatientPrintoutLegend />
          {images.map((image) => (
            <div key={image.imageId} className="vai-mt-4">
              <div
                className={classNames(
                  'vai-h-96 vai-flex vai-break-inside-avoid vai-mb-8',
                  {
                    '!vai-h-[25em] !vai-w-[30em]': !isMobile,
                  },
                )}
              >
                <PatientDownloadableImage
                  activeImage={image}
                  useSidebarRedesign
                  position={position}
                  estimatedVisitId={visitSummary?.estimatedVisitId ?? undefined}
                />
              </div>
              <div>
                {getTeethFilteredByImageView(
                  visitSummary?.teeth,
                  image,
                  selectedTeethPreview,
                  true,
                ).map((tooth) => (
                  <PatientPrintoutTooth
                    isMobile={isMobile}
                    isPerioPatient={!!visitSummary?.isPerioPatient}
                    key={tooth.toothId}
                    visitSummaryTooth={tooth}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
